import {useEffect, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  FieldValues,
  FormState,
  useForm as originalUseForm,
  SubmitHandler,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';

export type UseFormReturnValues<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
> = Omit<UseFormReturn<TFieldValues, TContext>, 'handleSubmit'> & {
  disableActions: boolean;
  /** lock is needed to for useLock/safeCallback as we need to allow closing the dialog during the submitting process in  most forms */
  lock: boolean;
  handleSubmit: () => Promise<void>;
};

type FormSubmitEventType = 'started' | 'done';
class FormSubmitEvent extends CustomEvent<FormSubmitEventType> {
  public constructor(detail: FormSubmitEventType) {
    super('submitting', {detail});
  }
}

export type EFormState = FormState<object>;

export const useForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
>(
  config: UseFormProps<TFieldValues, TContext> & {
    onSubmit: SubmitHandler<TFieldValues>;
    validateInitially?: boolean;
  }
): UseFormReturnValues<TFieldValues, TContext> => {
  const {validateInitially, onSubmit, ...originalConfig} = config;
  const [reallySubmitted, setReallySubmitted] = useState(false);
  const useFormReturnValues = originalUseForm<TFieldValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false,
    ...originalConfig,
  });

  useEffect(() => {
    if (validateInitially) {
      useFormReturnValues.trigger();
    }
  }, [validateInitially, useFormReturnValues]);

  useEffect(() => {
    if (useFormReturnValues.formState.isSubmitting) {
      window.dispatchEvent(new FormSubmitEvent('started'));
    } else {
      window.dispatchEvent(new FormSubmitEvent('done'));
    }
  }, [useFormReturnValues.formState.isSubmitting]);

  const wrappedOnSubmit = async (
    data: TFieldValues,
    event?: React.BaseSyntheticEvent
  ): Promise<any> => {
    const returnValue = await onSubmit(data, event);
    window.dispatchEvent(new FormSubmitEvent('done'));
    return returnValue;
  };

  useEffect(() => {
    setReallySubmitted(
      useFormReturnValues.formState.isSubmitted &&
        useFormReturnValues.formState.isValid
    );
    // we only want to re-evaluate if the form was submitted when the submitCount changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useFormReturnValues.formState.submitCount]);

  return {
    ...useFormReturnValues,
    handleSubmit: useFormReturnValues.handleSubmit(wrappedOnSubmit),
    disableActions: useFormReturnValues.formState.isSubmitting,
    lock:
      useFormReturnValues.formState.isDirty &&
      !(useFormReturnValues.formState.isSubmitting || reallySubmitted),
  };
};
